// Extend the existing Element UI 2 viewer once, including viewers appended to body.
// Keep the original image loading, toolbar, keyboard controls and close behavior.
export function installImageViewerTouch(Vue) {
  const Image = Vue.component('ElImage')
  Image.options.components.ImageViewer = Vue.extend({
    extends: Image.options.components.ImageViewer,
    computed: {
      imgStyle() {
        const { scale, deg, offsetX, offsetY, enableTransition } = this.transform
        return {
          transform: `translate(${offsetX}px, ${offsetY}px) scale(${scale}) rotate(${deg}deg)`,
          transition: enableTransition ? 'transform .3s' : '',
          ...(this.mode.name === 'contain' ? { maxWidth: '100%', maxHeight: '100%' } : {}),
        }
      },
    },
    watch: { index() { this._imageTouch = null } },
    mounted() {
      const wrapper = this.$el
      const canvas = this.$el.querySelector('.el-image-viewer__canvas')
      const point = touches => ({
        x: touches.length > 1 ? (touches[0].clientX + touches[1].clientX) / 2 : touches[0].clientX,
        y: touches.length > 1 ? (touches[0].clientY + touches[1].clientY) / 2 : touches[0].clientY,
        distance: touches.length > 1 ? Math.hypot(touches[0].clientX - touches[1].clientX, touches[0].clientY - touches[1].clientY) : 0,
      })
      const start = event => {
        if (event.target.closest('.el-image-viewer__btn')) return
        const img = canvas.querySelector('img')
        if (this.loading || !img || !img.naturalWidth || !event.touches.length) return
        event.stopPropagation()
        if (event.cancelable) event.preventDefault()
        const center = canvas.getBoundingClientRect()
        this._imageTouch = {
          ...point(event.touches), count: event.touches.length,
          scale: this.transform.scale, offsetX: this.transform.offsetX, offsetY: this.transform.offsetY,
          centerX: center.left + center.width / 2, centerY: center.top + center.height / 2,
          pinched: event.touches.length > 1 || Boolean(this._imageTouch && this._imageTouch.pinched),
          maskTap: event.target.classList.contains('el-image-viewer__mask'),
          moved: false, dx: 0, dy: 0,
        }
        this.transform.enableTransition = false
      }
      const move = event => {
        const gesture = this._imageTouch
        if (!gesture || !event.touches.length) return
        event.stopPropagation()
        if (event.cancelable) event.preventDefault()
        if (event.touches.length !== gesture.count) { start(event); return }
        const current = point(event.touches)
        gesture.dx = current.x - gesture.x
        gesture.dy = current.y - gesture.y
        gesture.moved = gesture.moved || Math.abs(gesture.dx) > 3 || Math.abs(gesture.dy) > 3
        if (gesture.count > 1 && gesture.distance > 0) {
          const scale = Math.max(0.2, Math.min(8, gesture.scale * current.distance / gesture.distance))
          const ratio = scale / gesture.scale
          this.transform.scale = scale
          // Keep the point between the fingers anchored while scaling and moving.
          this.transform.offsetX = current.x - gesture.centerX - (gesture.x - gesture.centerX - gesture.offsetX) * ratio
          this.transform.offsetY = current.y - gesture.centerY - (gesture.y - gesture.centerY - gesture.offsetY) * ratio
        } else {
          this.transform.offsetX = gesture.offsetX + gesture.dx
          this.transform.offsetY = gesture.offsetY + gesture.dy
        }
      }
      const end = event => {
        const gesture = this._imageTouch
        if (!gesture) return
        event.stopPropagation()
        if (event.cancelable) event.preventDefault()
        if (event.type !== 'touchcancel' && event.touches.length) { start(event); return }
        this._imageTouch = null
        if (event.type !== 'touchcancel' && gesture.maskTap && !gesture.moved && !gesture.pinched) { this.handleMaskClick(); return }
        if (event.type !== 'touchcancel' && !gesture.pinched && gesture.scale <= 1 && this.mode.name === 'contain' && !this.isSingle &&
          Math.abs(gesture.dx) > 60 && Math.abs(gesture.dx) > Math.abs(gesture.dy) * 1.5) {
          if (gesture.dx < 0) this.next()
          else this.prev()
        }
      }
      const listeners = { touchstart: start, touchmove: move, touchend: end, touchcancel: end }
      Object.keys(listeners).forEach(type => wrapper.addEventListener(type, listeners[type], { passive: false }))
      this._removeImageTouch = () => Object.keys(listeners).forEach(type => wrapper.removeEventListener(type, listeners[type]))
    },
    beforeDestroy() {
      if (this._removeImageTouch) this._removeImageTouch()
      this._imageTouch = null
      if (this._keyDownHandler) this.deviceSupportUninstall()
    },
  })
}
